import { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate, Link, useParams } from "react-router-dom";
import Flatpickr from "react-flatpickr";
import "flatpickr/dist/flatpickr.min.css";
import Spinner from "../util/Spinner";
import { fetchTVShowList } from "../slice/tvShowListsSlice";
import {
  fetchTVShowListItem,
  updateTVShowListItem,
  deleteTVShowListItem,
} from "../slice/tvShowListItemsSlice";
import { fetchRatings } from "../slice/ratingSlice";
import TVShowListItemComment from "./TVShowListItemComment";

const TVShowListItem = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  // params from url
  const { sid, listsid } = useParams();
  const [shouldRefresh, setShouldRefresh] = useState(true);

  // tv show list
  const { tvShowList, isLoading } = useSelector((state) => {
    return state?.tvShowLists;
  });

  // tv show list item
  const { tvShowListItem, isLoadingItem } = useSelector((state) => {
    return state?.tvShowListItems;
  });

  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const reduxRatings = useSelector((state) => state.ratings.ratings);
  const [editableListItem, setEditableListItem] = useState();
  const [ratings, setRatings] = useState([]);

  // fetch the tv show list
  useEffect(() => {
    if (listsid) {
      console.log("calling fetch tv show list with sid ", listsid);
      dispatch(fetchTVShowList(listsid));
    }
  }, [dispatch, listsid, shouldRefresh]);

  useEffect(() => {
    if (sid) {
      console.log("fetching ratings...");
      dispatch(fetchRatings());
      console.log("calling fetch tv show list item with sid ", sid);
      dispatch(fetchTVShowListItem(sid));
    }
  }, [dispatch, sid]);

  useEffect(() => {
    if (tvShowListItem) {
      console.log("setting editable list item...");
      setEditableListItem({
        sid: tvShowListItem?.sid,
        dateStarted: tvShowListItem?.dateStarted,
        rating: tvShowListItem?.rating,
        comments: tvShowListItem?.comments,
      });
    }
  }, [tvShowListItem]);

  const deleteHandler = async (e) => {
    e.preventDefault();
    try {
      console.log("deleting tv show list item with sid ", sid);
      setIsDeleting(true);
      const resultAction = await dispatch(deleteTVShowListItem(sid));
      if (deleteTVShowListItem.fulfilled.match(resultAction)) {
        navigate(`/tvshows/${listsid}`);
        console.log("successfully deleted tv show list item with sid ", sid);
      } else {
        console.log("delete tv show list item failed");
      }
    } catch (error) {
      console.log("delete tv show list item threw an exception and failed");
    } finally {
      setIsDeleting(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      console.log("updating tv show list item with sid ", sid);
      setIsSaving(true);
      const putData = {
        sid: editableListItem?.sid,
        dateStarted: editableListItem?.dateStarted,
        rating: editableListItem?.rating,
        commentUpdates: editableListItem?.comments,
      };
      console.log("putData is ", editableListItem);
      const resultAction = await dispatch(updateTVShowListItem(putData));
      if (updateTVShowListItem.fulfilled.match(resultAction)) {
        console.log("successfully updated tv show list item!");
        console.log("refreshing tv show list item with sid ", sid);
        dispatch(fetchTVShowListItem(sid));
      } else {
        console.log("update tv show list item failed");
      }
    } catch (error) {
      console.log("update tv show list item threw an exception and failed");
    } finally {
      setIsSaving(false);
      setIsEditing(false);
    }
  };

  const handleRatingSelected = (e) => {
    const value = e.target.value;
    console.log("handleRatingSelected value ", value);
    const rating = parseInt(value, 10); // convert to integer in base 10
    const selectedRating = reduxRatings.find((r) => r.value === rating);
    console.log("selected rating ", selectedRating);
    setEditableListItem((prev) => ({
      ...prev,
      ["rating"]: selectedRating,
    }));
  };

  const setDateStarted = (dateStarted) => {
    console.log("setting date started to ", dateStarted);
    setEditableListItem((prev) => ({
      ...prev,
      ["dateStarted"]: dateStarted,
    }));
  };

  const updateComment = (index, text) => {
    console.log("updating comment at " + index + " to " + text);
    setEditableListItem((prev) => ({
      ...prev,
      comments: prev?.comments?.map((c, i) =>
        i == index ? { ...c, comment: text } : c
      ),
    }));
  };

  const removeComment = (index) => {
    console.log("removing comment from list: ", index);
    setEditableListItem((prev) => ({
      ...prev,
      comments: prev?.comments?.filter((c, i) => i !== index),
    }));
  };

  const addComment = () => {
    setEditableListItem((prev) => ({
      ...prev,
      comments: [...editableListItem?.comments, { id: null, comment: "" }],
    }));
  };

  const actors = () => {
    return tvShowListItem?.tvShow?.actors?.map((actor, index) => (
      <span key={actor.sid}>
        <Link to={`/tvshows2/${listsid}/${actor.sid}`} state={{ actor }}>
          {actor?.name}
        </Link>
        {index < tvShowListItem?.tvShow?.actors.length - 1 ? ", " : ""}
      </span>
    ));
  };

  const genres = () => {
    console.log("!!! tvShowListItem: ", tvShowListItem);
    return tvShowListItem?.tvShow?.genres
      ?.map((genre, index) => genre?.genreTmdbId)
      .join(", ");
  };

  const inputClasses =
    "w-full border border-stone-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-teal-600";
  const fieldLabelClasses = "block text-sm font-medium text-stone-500 mb-1";

  return (
    <div className="max-w-2xl mx-auto p-4 md:p-6">
      {isLoading || isLoadingItem ? (
        <Spinner />
      ) : (
        <>
          {/* edit view */}
          {isEditing ? (
            <form
              onSubmit={handleSubmit}
              className="card p-4 md:p-6 space-y-4"
            >
              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setIsEditing(false)}
                  className="btn-secondary"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSaving}
                  className="btn-primary"
                >
                  {isSaving ? "Saving..." : "Save"}
                </button>
              </div>
              <div>
                <span className={fieldLabelClasses}>Title</span>
                <p className="text-stone-800">
                  {tvShowListItem?.tvShow?.title}
                </p>
              </div>
              <div>
                <label className={fieldLabelClasses}>Rating</label>
                {editableListItem?.rating !== undefined &&
                  reduxRatings !== undefined &&
                  reduxRatings.length > 0 && (
                    <select
                      id="dropdown"
                      value={editableListItem?.rating?.value}
                      onChange={handleRatingSelected}
                      className={inputClasses}
                    >
                      <option value="-1" disabled>
                        Rating
                      </option>
                      {reduxRatings.map((r, index) => (
                        <option key={index} value={r.value}>
                          {r.description}
                        </option>
                      ))}
                    </select>
                  )}
              </div>
              <div>
                <label className={fieldLabelClasses}>Date started</label>
                <Flatpickr
                  className={inputClasses}
                  value={editableListItem?.dateStarted}
                  onChange={(selectedDates) =>
                    setDateStarted(selectedDates[0])
                  }
                  options={{ dateFormat: "Y-m-d" }}
                />
              </div>
              <div>
                <span className={fieldLabelClasses}>Comments</span>
                <div className="space-y-2">
                  {editableListItem?.comments?.map((comment, index) => (
                    <div
                      key={index}
                      className="flex flex-col sm:flex-row gap-2"
                    >
                      <input
                        className={`${inputClasses} flex-1`}
                        key={comment?.sid}
                        type="text"
                        placeholder="Comment"
                        value={comment?.comment}
                        onChange={(e) => updateComment(index, e.target.value)}
                      />
                      <button
                        type="button"
                        onClick={() => removeComment(index)}
                        className="btn-secondary shrink-0"
                      >
                        Remove
                      </button>
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={addComment}
                    className="btn-secondary"
                  >
                    Add
                  </button>
                </div>
              </div>
              {/* end edit view */}
            </form>
          ) : (
            <>
              {/* detail view */}
              <div className="flex justify-end gap-2 mb-4">
                <button className="btn-primary" onClick={() => setIsEditing(true)}>
                  Edit
                </button>
                <button
                  className="btn-secondary"
                  disabled={isDeleting}
                  onClick={deleteHandler}
                >
                  {isDeleting ? "Deleting..." : "Delete"}
                </button>
              </div>
              <dl className="card p-4 md:p-6 divide-y divide-stone-200">
                <div className="py-3 first:pt-0">
                  <dt className="text-sm font-medium text-stone-500">List</dt>
                  <dd className="mt-1 text-stone-800">
                    <Link
                      to={`/tvshows/${listsid}`}
                      className="text-teal-700 hover:underline"
                    >
                      {tvShowList?.name}
                    </Link>
                  </dd>
                </div>
                <div className="py-3">
                  <dt className="text-sm font-medium text-stone-500">Title</dt>
                  <dd className="mt-1 text-stone-800">
                    {tvShowListItem?.tvShow?.title}
                  </dd>
                </div>
                <div className="py-3">
                  <dt className="text-sm font-medium text-stone-500">
                    Rating
                  </dt>
                  <dd className="mt-1 text-stone-800">
                    {tvShowListItem?.rating?.description}
                  </dd>
                </div>
                <div className="py-3">
                  <dt className="text-sm font-medium text-stone-500">
                    Date started
                  </dt>
                  <dd className="mt-1 text-stone-800">
                    {tvShowListItem?.dateStarted}
                  </dd>
                </div>
                <div className="py-3">
                  <dt className="text-sm font-medium text-stone-500">
                    Genre
                  </dt>
                  <dd className="mt-1 text-stone-800">{genres()}</dd>
                </div>
                <div className="py-3">
                  <dt className="text-sm font-medium text-stone-500">
                    Year started
                  </dt>
                  <dd className="mt-1 text-stone-800">
                    {tvShowListItem?.tvShow?.yearStart}
                  </dd>
                </div>
                <div className="py-3">
                  <dt className="text-sm font-medium text-stone-500">
                    Language
                  </dt>
                  <dd className="mt-1 text-stone-800">
                    {tvShowListItem?.tvShow?.language}
                  </dd>
                </div>
                <div className="py-3">
                  <dt className="text-sm font-medium text-stone-500">
                    Network
                  </dt>
                  <dd className="mt-1 text-stone-800">
                    {tvShowListItem?.tvShow?.network}
                  </dd>
                </div>
                <div className="py-3">
                  <dt className="text-sm font-medium text-stone-500">
                    Overview
                  </dt>
                  <dd className="mt-1 text-stone-800">
                    {tvShowListItem?.tvShow?.overview}
                  </dd>
                </div>
                <div className="py-3">
                  <dt className="text-sm font-medium text-stone-500">
                    Actors
                  </dt>
                  <dd className="mt-1 text-stone-800">{actors()}</dd>
                </div>
                <div className="py-3 last:pb-0">
                  <dt className="text-sm font-medium text-stone-500">
                    Comments
                  </dt>
                  <dd className="mt-1">
                    <ul className="list-disc pl-5 space-y-1 text-stone-800">
                      {tvShowListItem?.comments?.map((comment) => {
                        return (
                          <TVShowListItemComment
                            key={comment?.sid}
                            comment={comment}
                          ></TVShowListItemComment>
                        );
                      })}
                    </ul>
                  </dd>
                </div>
              </dl>
            </>
          )}
          {/* end detail view */}
        </>
      )}
    </div>
  );
};

export default TVShowListItem;
